import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../App';

/**
 * Gate an action behind the photo-usage consent.
 * `requireConsent(action)` runs `action` immediately when the user already
 * consented, otherwise opens the dialog and runs it once accepted.
 */
export const usePhotoConsent = () => {
  const { user, updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const pendingRef = useRef(null);

  const hasConsent = Boolean(user?.photo_consent);

  const requireConsent = useCallback((action) => {
    if (hasConsent) {
      action?.();
      return;
    }
    pendingRef.current = action || null;
    setOpen(true);
  }, [hasConsent]);

  const onAccepted = useCallback(() => {
    updateUser({ photo_consent: true });
    const action = pendingRef.current;
    pendingRef.current = null;
    // let the dialog unmount before e.g. opening a native file picker
    if (action) setTimeout(action, 50);
  }, [updateUser]);

  const onOpenChange = useCallback((next) => {
    setOpen(next);
    if (!next) pendingRef.current = null;
  }, []);

  return { hasConsent, consentOpen: open, onConsentOpenChange: onOpenChange, onConsentAccepted: onAccepted, requireConsent, retentionDays: user?.retention_days || 90 };
};
